import { FOOTER_PREMIUM } from "../common/footer";
import { PULSE_HEADER } from "../common/header";

/**
 * World-Class Weekly Digest Template
 * Features: Key metrics cards, top bots table, insights section, trends
 */
export const weeklyDigest = `
<mjml>
  <mj-head>
    <mj-title>Your Weekly Activity Digest</mj-title>
    <mj-preview>Here's what happened with your chatbots this week</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .trend-up { color: #10B981; font-weight: 600; }
      .trend-down { color: #EF4444; font-weight: 600; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#f1f5f9">
    \${PULSE_HEADER("Weekly Digest", "{{report_date_range}}")}

    <!-- Introduction -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text font-size="24px" color="#0f172a" font-weight="700" padding-bottom="12px">
          Hello {{first_name}},
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          Here's your weekly activity summary for {{product_name}}. Review your key metrics and see how your chatbots performed this week.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Key Metrics Header -->
    <mj-section background-color="#ffffff" padding="30px 30px 15px" border-radius="16px 16px 0 0">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Performance
        </mj-text>
        <mj-text font-size="22px" color="#0f172a" font-weight="700">
          Key Metrics This Week
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Metrics Row 1 -->
    <mj-section background-color="#ffffff" padding="10px 20px">
      <mj-column width="48%" padding="10px" background-color="#f0f9ff" border-radius="12px">
        <mj-text align="center" font-size="36px" color="#1939B7" font-weight="700" padding-bottom="0">
          {{total_conversations}}
        </mj-text>
        <mj-text align="center" font-size="13px" color="#64748b" text-transform="uppercase" letter-spacing="1px" padding-top="5px" padding-bottom="5px">
          Conversations
        </mj-text>
        <mj-text align="center" font-size="14px" css-class="{{conversations_trend_class}}" padding-top="0">
          {{conversations_trend}} vs last week
        </mj-text>
      </mj-column>
      <mj-column width="48%" padding="10px" background-color="#ecfdf5" border-radius="12px">
        <mj-text align="center" font-size="36px" color="#10B981" font-weight="700" padding-bottom="0">
          {{total_messages}}
        </mj-text>
        <mj-text align="center" font-size="13px" color="#64748b" text-transform="uppercase" letter-spacing="1px" padding-top="5px" padding-bottom="5px">
          Messages
        </mj-text>
        <mj-text align="center" font-size="14px" css-class="{{messages_trend_class}}" padding-top="0">
          {{messages_trend}} vs last week
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Metrics Row 2 -->
    <mj-section background-color="#ffffff" padding="10px 20px 30px" border-radius="0 0 16px 16px">
      <mj-column width="48%" padding="10px" background-color="#faf5ff" border-radius="12px">
        <mj-text align="center" font-size="36px" color="#8B5CF6" font-weight="700" padding-bottom="0">
          {{unique_users}}
        </mj-text>
        <mj-text align="center" font-size="13px" color="#64748b" text-transform="uppercase" letter-spacing="1px" padding-top="5px" padding-bottom="5px">
          Unique Users
        </mj-text>
        <mj-text align="center" font-size="14px" css-class="{{users_trend_class}}" padding-top="0">
          {{users_trend}} vs last week
        </mj-text>
      </mj-column>
      <mj-column width="48%" padding="10px" background-color="#fef3c7" border-radius="12px">
        <mj-text align="center" font-size="36px" color="#F59E0B" font-weight="700" padding-bottom="0">
          {{satisfaction_score}}%
        </mj-text>
        <mj-text align="center" font-size="13px" color="#64748b" text-transform="uppercase" letter-spacing="1px" padding-top="5px" padding-bottom="5px">
          Satisfaction
        </mj-text>
        <mj-text align="center" font-size="14px" css-class="{{satisfaction_trend_class}}" padding-top="0">
          {{satisfaction_trend}} vs last week
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Top Performing Bots -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Leaderboard
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="20px">
          Top Performing Chatbots
        </mj-text>
        <mj-table>
          <tr style="border-bottom: 2px solid #e2e8f0; background-color: #f8fafc;">
            <th style="padding: 12px; text-align: left; font-size: 13px; color: #64748b; text-transform: uppercase;">Chatbot</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Chats</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Avg. Time</th>
            <th style="padding: 12px; text-align: center; font-size: 13px; color: #64748b; text-transform: uppercase;">Rating</th>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #1939B7; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">1</span>
              {{bot_1_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_1_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_1_duration}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #F59E0B;">{{bot_1_rating}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #e2e8f0;">
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #64748B; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">2</span>
              {{bot_2_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_2_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_2_duration}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #F59E0B;">{{bot_2_rating}}</td>
          </tr>
          <tr>
            <td style="padding: 12px; font-size: 14px; font-weight: 600; color: #0f172a;">
              <span style="background: #94A3B8; color: white; width: 20px; height: 20px; border-radius: 50%; display: inline-block; text-align: center; line-height: 20px; font-size: 11px; margin-right: 8px;">3</span>
              {{bot_3_name}}
            </td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_3_conversations}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px;">{{bot_3_duration}}</td>
            <td style="padding: 12px; text-align: center; font-size: 14px; font-weight: 600; color: #F59E0B;">{{bot_3_rating}}</td>
          </tr>
        </mj-table>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Weekly Insights -->
    <mj-section background-color="#f0f9ff" padding="30px" border-radius="16px" border-left="4px solid #1939B7">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Insights
        </mj-text>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="15px">
          Weekly Analysis
        </mj-text>
        <mj-text font-size="15px" color="#334155" padding-bottom="10px">
          <strong style="color: #1939B7;">Peak Hours:</strong> {{peak_hours}}
        </mj-text>
        <mj-text font-size="15px" color="#334155" padding-bottom="10px">
          <strong style="color: #1939B7;">Most Common Queries:</strong> {{common_queries}}
        </mj-text>
        <mj-text font-size="15px" color="#334155">
          <strong style="color: #1939B7;">Recommendation:</strong> {{weekly_recommendation}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- CTA Section -->
    <mj-section background-color="#1939B7" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="24px" color="#ffffff" font-weight="700" align="center" padding-bottom="10px">
          Want to See More Details?
        </mj-text>
        <mj-text font-size="15px" color="rgba(255,255,255,0.9)" align="center" padding-bottom="20px">
          Visit your dashboard for comprehensive analytics and detailed reports.
        </mj-text>
        <mj-button background-color="#2DD4BF" color="#0F172A" href="{{dashboard_url}}" border-radius="8px" padding="18px 45px" font-size="17px" font-weight="700">
          View Full Report &#8594;
        </mj-button>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "weeklyDigest",
  name: "Weekly Digest - Premium",
  category: "pulse",
  purpose: "pulse" as const,
  description:
    "A world-class weekly digest with colorful metric cards, ranked bot leaderboard, insights section with recommendations, and compelling dashboard CTA.",
  features: [
    "Colorful metric cards with trends",
    "Ranked chatbot leaderboard",
    "Weekly insights and recommendations",
    "Week-over-week comparisons",
    "Premium CTA styling",
    "Visual hierarchy with icons",
  ],
  subject: "&#128202; Your Weekly Activity Digest - {{report_date_range}}",
  subTitle: "Here's what happened with your chatbots this week",
  mjmlTemplate: weeklyDigest,
  tags: ["pulse", "weekly", "digest", "analytics", "report", "metrics"],
};
